import {
  TEMA_PERMISSIONS,
  TEMA_ROUTE_IDS,
  TEMA_ROUTE_PATHS,
} from "./temaRouteConstants";
import {
  LazyActivityGeneratorPage,
  LazyConceptQuestionsPage,
  LazyContentSimplifierPage,
  LazyDashboardPage,
  LazyDifficultyBalancerPage,
  LazyDoubtSolverPage,
  LazyExplainDifferentlyPage,
  LazyLibraryPage,
  LazyProjectBuilderPage,
  LazyQuestionPaperPage,
  LazyQuestionVariationsPage,
  LazyQuickAssessmentPage,
  LazyQuickSheetsPage,
  LazyRealWorldTaskGeneratorPage,
  LazySectionBuilderPage,
  LazySectionHubPage,
  LazySlideDeckGeneratorPage,
  LazyTeachThisTopicPage,
} from "./lazyPages";

const WORKSPACE_PERMISSIONS = [
  TEMA_PERMISSIONS.WORKSPACE_ACCESS,
  TEMA_PERMISSIONS.TOOLS_GENERATE,
];

const SECTION_HUB_PATHS = {
  "Create Questions": TEMA_ROUTE_PATHS.CREATE_QUESTIONS,
  "Teach and Prepare": TEMA_ROUTE_PATHS.TEACH_PREPARE,
  "Build Assessments": TEMA_ROUTE_PATHS.BUILD_ASSESSMENTS,
  "Assign Enrichment": TEMA_ROUTE_PATHS.ASSIGN_ENRICHMENT,
};

function sectionHubRoute({ id, path, label, documentTitle }) {
  return {
    id,
    path,
    component: LazySectionHubPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle,
    breadcrumbs: [
      { label: "ITEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label },
    ],
  };
}

function workspaceRoute({
  id,
  path,
  aliases = [],
  component,
  label,
  section,
  documentTitle,
}) {
  return {
    id,
    path,
    aliases,
    component,
    module: "tema",
    requiresAuth: true,
    permissions: WORKSPACE_PERMISSIONS,
    workspace: true,
    lazy: true,
    documentTitle,
    breadcrumbs: [
      { label: "ITEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      ...(section
        ? [
            {
              label: section,
              ...(SECTION_HUB_PATHS[section]
                ? { href: SECTION_HUB_PATHS[section] }
                : {}),
            },
          ]
        : []),
      { label },
    ],
  };
}

export const TEMA_ROUTES = [
  {
    id: TEMA_ROUTE_IDS.DASHBOARD,
    path: TEMA_ROUTE_PATHS.DASHBOARD,
    aliases: ["#dashboard"],
    component: LazyDashboardPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "ITEMA",
    breadcrumbs: [{ label: "Dashboard" }],
  },
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.TEACH_PREPARE,
    path: TEMA_ROUTE_PATHS.TEACH_PREPARE,
    label: "Teach & prepare",
    documentTitle: "ITEMA",
  }),
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.CREATE_QUESTIONS,
    path: TEMA_ROUTE_PATHS.CREATE_QUESTIONS,
    label: "Create Questions & Sheets",
    documentTitle: "ITEMA",
  }),
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.BUILD_ASSESSMENTS,
    path: TEMA_ROUTE_PATHS.BUILD_ASSESSMENTS,
    label: "Build Assessments",
    documentTitle: "ITEMA",
  }),
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.ASSIGN_ENRICHMENT,
    path: TEMA_ROUTE_PATHS.ASSIGN_ENRICHMENT,
    label: "Assign Enrichment",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUICK_SHEETS,
    path: TEMA_ROUTE_PATHS.QUICK_SHEETS,
    component: LazyQuickSheetsPage,
    label: "Quick Sheets",
    section: "Create Questions",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.CONCEPT_QUESTIONS,
    path: TEMA_ROUTE_PATHS.CONCEPT_QUESTIONS,
    component: LazyConceptQuestionsPage,
    label: "Concept → Questions",
    section: "Create Questions",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUESTION_VARIATIONS,
    path: TEMA_ROUTE_PATHS.QUESTION_VARIATIONS,
    component: LazyQuestionVariationsPage,
    label: "Question Variations",
    section: "Create Questions",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUICK_ASSESSMENT,
    path: TEMA_ROUTE_PATHS.QUICK_ASSESSMENT,
    component: LazyQuickAssessmentPage,
    label: "Quick Assessment",
    section: "Create Questions",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.TEACH_THIS_TOPIC,
    path: TEMA_ROUTE_PATHS.TEACH_THIS_TOPIC,
    component: LazyTeachThisTopicPage,
    label: "Teach This Topic",
    section: "Teach and Prepare",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.EXPLAIN_DIFFERENTLY,
    path: TEMA_ROUTE_PATHS.EXPLAIN_DIFFERENTLY,
    component: LazyExplainDifferentlyPage,
    label: "Explain Differently",
    section: "Teach and Prepare",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.CONTENT_SIMPLIFIER,
    path: TEMA_ROUTE_PATHS.CONTENT_SIMPLIFIER,
    component: LazyContentSimplifierPage,
    label: "Content Simplifier",
    section: "Teach and Prepare",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUESTION_PAPER,
    path: TEMA_ROUTE_PATHS.QUESTION_PAPER,
    aliases: ["#question-paper"],
    component: LazyQuestionPaperPage,
    label: "Question Paper Generator",
    section: "Build Assessments",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.SECTION_BUILDER,
    path: TEMA_ROUTE_PATHS.SECTION_BUILDER,
    component: LazySectionBuilderPage,
    label: "Section Builder",
    section: "Build Assessments",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.DIFFICULTY_BALANCER,
    path: TEMA_ROUTE_PATHS.DIFFICULTY_BALANCER,
    component: LazyDifficultyBalancerPage,
    label: "Difficulty Balancer",
    section: "Build Assessments",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.DOUBT_SOLVER,
    path: TEMA_ROUTE_PATHS.DOUBT_SOLVER,
    component: LazyDoubtSolverPage,
    label: "Doubt Solver",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.SLIDE_DECK_GENERATOR,
    path: TEMA_ROUTE_PATHS.SLIDE_DECK_GENERATOR,
    aliases: ["#slide-deck"],
    component: LazySlideDeckGeneratorPage,
    label: "Slide Deck Generator",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.PROJECT_BUILDER,
    path: TEMA_ROUTE_PATHS.PROJECT_BUILDER,
    component: LazyProjectBuilderPage,
    label: "Project Builder",
    section: "Assign Enrichment",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.ACTIVITY_GENERATOR,
    path: TEMA_ROUTE_PATHS.ACTIVITY_GENERATOR,
    component: LazyActivityGeneratorPage,
    label: "Activity Generator",
    section: "Assign Enrichment",
    documentTitle: "ITEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.REAL_WORLD_TASK,
    path: TEMA_ROUTE_PATHS.REAL_WORLD_TASK,
    aliases: ["#real-world-task-generator"],
    component: LazyRealWorldTaskGeneratorPage,
    label: "Real-World Task Generator",
    section: "Assign Enrichment",
    documentTitle: "ITEMA",
  }),
  {
    id: TEMA_ROUTE_IDS.LIBRARY,
    path: TEMA_ROUTE_PATHS.LIBRARY,
    aliases: ["#my-library", "#saved-lessons"],
    component: LazyLibraryPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "ITEMA",
    breadcrumbs: [
      { label: "ITEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label: "Saved Lessons" },
    ],
  },
];

export const TEMA_NOT_FOUND_ROUTE = {
  id: TEMA_ROUTE_IDS.NOT_FOUND,
  path: null,
  component: null,
  module: "tema",
  requiresAuth: true,
  permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
  workspace: false,
  lazy: false,
  documentTitle: "ITEMA",
  breadcrumbs: [{ label: "Page not found" }],
};
